import { useState, useEffect, useRef } from 'react';
import { useNavigate, Link, useSearchParams } from 'react-router-dom';
import { supabase } from '@/integrations/supabase/client';
import { lovable } from '@/integrations/lovable/index';

import { Layout } from '@/components/layout';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Checkbox } from '@/components/ui/checkbox';
import { InputOTP, InputOTPGroup, InputOTPSlot } from '@/components/ui/input-otp';
import { toast } from '@/hooks/use-toast';
import { Home, Mail, Lock, User, Eye, EyeOff, Sparkles, Camera, FileText, Globe2, ShieldCheck, Zap, Star } from 'lucide-react';
import portraitMarcus from '@/assets/testimonials/marcus.jpg';
import { z } from 'zod';

const loginSchema = z.object({
  email: z.string().email('Please enter a valid email'),
  password: z.string().min(1, 'Password is required'),
});

// Signup is passwordless: email + 6-digit code. A password can be set right
// after verification (optional) or later via the magic-link reset flow.
const signupSchema = z.object({
  email: z.string().email('Please enter a valid email'),
});

const OTP_RESEND_COOLDOWN_SECONDS = 60;

export default function Auth() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [errors, setErrors] = useState<{ email?: string; password?: string }>({});
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [isRecoveryMode, setIsRecoveryMode] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [acceptTerms, setAcceptTerms] = useState(false);
  const [acceptMarketing, setAcceptMarketing] = useState(false);
  // Passwordless signup flow: form → otp (enter 6-digit code) → set-password (optional)
  const [signupStep, setSignupStep] = useState<'form' | 'otp' | 'set-password'>('form');
  const [otpCode, setOtpCode] = useState('');
  const [resendIn, setResendIn] = useState(0);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  // The auth-state listener below redirects as soon as a session appears. While
  // the OTP flow is mid-verification we stay on this page to offer the optional
  // set-password step, so the listener consults this ref before navigating.
  const otpFlowActiveRef = useRef(false);
  otpFlowActiveRef.current = signupStep !== 'form';

  // Resend-code cooldown ticker
  useEffect(() => {
    if (resendIn <= 0) return;
    const timer = setTimeout(() => setResendIn(resendIn - 1), 1000);
    return () => clearTimeout(timer);
  }, [resendIn]);

  // Validate a `next` param as a SAFE same-origin relative path. Returns the
  // path if it starts with a single `/` and is not protocol-relative (`//`),
  // otherwise null. Used so external callers (e.g. the OAuth consent page)
  // can round-trip a user through sign-in back to a specific in-app URL.
  const safeNextPath = (value: string | null): string | null => {
    if (!value) return null;
    if (!value.startsWith('/') || value.startsWith('//')) return null;
    return value;
  };

  // Build the post-auth destination. Supports three shapes:
  //   ?next=create             → legacy homepage CTA, forward into create flow
  //   ?next=/some/relative/url → arbitrary same-origin redirect (OAuth consent, etc.)
  //   (none)                   → homepage
  const getPostAuthDestination = () => {
    const next = searchParams.get('next');
    const pendingCreate = sessionStorage.getItem('listbright_create_pending') === 'true';
    if (next === 'create' || pendingCreate) return '/?create=true';
    const safe = safeNextPath(next);
    if (safe) return safe;
    return '/';
  };

  // Absolute URL variant used for supabase redirect options (emailRedirectTo,
  // signInWithOAuth redirectTo). Keeps the same routing logic as above.
  const getPostAuthRedirectUrl = () => {
    const dest = getPostAuthDestination();
    return `${window.location.origin}${dest}`;
  };

  useEffect(() => {
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
      if (event === 'PASSWORD_RECOVERY') {
        setIsRecoveryMode(true);
        return;
      }
      if (session?.user && !isRecoveryMode && !otpFlowActiveRef.current) {
        navigate(getPostAuthDestination());
      }
    });

    supabase.auth.getSession().then(({ data: { session } }) => {
      if (session?.user && !isRecoveryMode && !otpFlowActiveRef.current) {
        navigate(getPostAuthDestination());
      }
    });

    return () => subscription.unsubscribe();
  }, [navigate, isRecoveryMode]);

  const validateForm = (isSignup: boolean) => {
    try {
      const schema = isSignup ? signupSchema : loginSchema;
      schema.parse({ email, password });
      setErrors({});
      return true;
    } catch (error) {
      if (error instanceof z.ZodError) {
        const fieldErrors: { email?: string; password?: string } = {};
        error.errors.forEach((err) => {
          if (err.path[0] === 'email') fieldErrors.email = err.message;
          if (err.path[0] === 'password') fieldErrors.password = err.message;
        });
        setErrors(fieldErrors);
      }
      return false;
    }
  };

  const getLoginErrorMessage = (error: { message: string }) => {
    const msg = error.message.toLowerCase();
    if (msg.includes('invalid login credentials')) {
      return 'Invalid email or password. Please check your credentials and try again.';
    }
    if (msg.includes('email not confirmed')) {
      return 'Please verify your email before logging in. Check your inbox for the confirmation link.';
    }
    if (msg.includes('too many requests') || msg.includes('rate limit')) {
      return 'Too many login attempts. Please wait a few minutes before trying again.';
    }
    if (msg.includes('network') || msg.includes('fetch')) {
      return 'Connection error. Please check your internet and try again.';
    }
    return error.message;
  };

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validateForm(false)) return;
    
    setIsLoading(true);
    try {
      const { error } = await supabase.auth.signInWithPassword({ email, password });
      
      if (error) {
        toast({
          title: 'Login failed',
          description: getLoginErrorMessage(error),
          variant: 'destructive',
        });
      } else {
        toast({ title: 'Welcome back!' });
      }
    } catch (err) {
      toast({
        title: 'Connection error',
        description: 'Unable to connect. Please check your internet connection.',
        variant: 'destructive',
      });
    }
    setIsLoading(false);
  };

  const getSignupErrorMessage = (error: { message: string }) => {
    const msg = error.message.toLowerCase();
    if (msg.includes('already registered') || msg.includes('already exists')) {
      return 'This email is already registered. Please login or use forgot password.';
    }
    if (msg.includes('password') && msg.includes('weak')) {
      return 'Password is too weak. Use at least 8 characters with uppercase, lowercase, and numbers.';
    }
    if (msg.includes('valid email') || msg.includes('invalid email')) {
      return 'Please enter a valid email address.';
    }
    if (msg.includes('too many requests') || msg.includes('rate limit')) {
      return 'Too many signup attempts. Please wait a few minutes before trying again.';
    }
    if (msg.includes('network') || msg.includes('fetch')) {
      return 'Connection error. Please check your internet and try again.';
    }
    return error.message;
  };

  // Send (or resend) the 6-digit signup code. Creates the account if the email
  // is new; for existing accounts it simply acts as a passwordless login code.
  const sendSignupCode = async (opts?: { shouldCreateUser?: boolean }): Promise<boolean> => {
    const shouldCreateUser = opts?.shouldCreateUser ?? true;
    setIsLoading(true);
    try {
      const { error } = await supabase.auth.signInWithOtp({
        email,
        options: {
          shouldCreateUser,
          data: {
            marketing_consent: acceptMarketing,
          },
        },
      });

      if (error) {
        // If the user doesn't exist and we asked for sign-in only, tell them to sign up.
        if (!shouldCreateUser && /not found|does not exist|no user|signups not allowed/i.test(error.message)) {
          toast({
            title: 'No account found',
            description: "We couldn't find an account with that email. Try the Sign Up tab instead.",
            variant: 'destructive',
          });
          return false;
        }
        toast({
          title: 'Could not send code',
          description: getSignupErrorMessage(error),
          variant: 'destructive',
        });
        return false;
      }
      setResendIn(OTP_RESEND_COOLDOWN_SECONDS);
      return true;
    } catch (err) {
      toast({
        title: 'Connection error',
        description: 'Unable to connect. Please check your internet connection.',
        variant: 'destructive',
      });
      return false;
    } finally {
      setIsLoading(false);
    }
  };

  const handleSignUp = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validateForm(true)) return;

    if (!acceptTerms) {
      toast({
        title: 'Terms acceptance required',
        description: 'Please accept the Terms and Conditions to create an account.',
        variant: 'destructive',
      });
      return;
    }

    const sent = await sendSignupCode();
    if (sent) {
      setOtpCode('');
      setSignupStep('otp');
      toast({
        title: 'Check your email',
        description: `We sent a 6-digit code to ${email}. Enter it below — no need to leave this page.`,
      });
    }
  };

  const handleVerifyOtp = async (code?: string) => {
    const token = code ?? otpCode;
    if (token.length !== 6) return;

    setIsLoading(true);
    try {
      const { data, error } = await supabase.auth.verifyOtp({
        email,
        token,
        type: 'email',
      });

      if (error || !data.session) {
        const msg = (error?.message || '').toLowerCase();
        toast({
          title: 'Invalid code',
          description: msg.includes('expired')
            ? 'That code has expired. Tap "Resend code" to get a fresh one.'
            : 'That code doesn\'t match. Double-check the 6 digits and try again.',
          variant: 'destructive',
        });
        setOtpCode('');
        return;
      }

      // Signed in — persist marketing consent to the profile
      if (data.user && acceptMarketing) {
        await supabase.from('profiles').update({
          marketing_consent: true,
          marketing_consent_date: new Date().toISOString(),
        }).eq('id', data.user.id);
      }

      // Detect existing-account sign-in vs a brand-new signup. If the account
      // was created more than a minute ago it's an existing user — never show
      // the "set password" step (which would silently overwrite their password).
      const createdAt = data.user?.created_at ? new Date(data.user.created_at).getTime() : Date.now();
      const isNewAccount = Date.now() - createdAt < 60_000;

      if (isNewAccount) {
        toast({
          title: 'Email verified! 🎉',
          description: 'One last step: create a password to sign in next time.',
        });
        setSignupStep('set-password');
      } else {
        toast({
          title: 'Welcome back!',
          description: 'Signed in with your email code.',
        });
        finishSignup();
      }
    } catch (err) {
      toast({
        title: 'Connection error',
        description: 'Unable to connect. Please check your internet connection.',
        variant: 'destructive',
      });
    } finally {
      setIsLoading(false);
    }
  };

  // Optional post-verification password. Skipping is fine — users can always
  // sign in with a code or set a password later via the reset flow.
  const handleSetSignupPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    if (newPassword.length < 8) {
      toast({
        title: 'Password too short',
        description: 'Password must be at least 8 characters.',
        variant: 'destructive',
      });
      return;
    }

    setIsLoading(true);
    const { error } = await supabase.auth.updateUser({ password: newPassword });
    setIsLoading(false);

    if (error) {
      toast({
        title: 'Could not save password',
        description: error.message,
        variant: 'destructive',
      });
      return;
    }
    finishSignup();
  };

  const finishSignup = () => {
    otpFlowActiveRef.current = false;
    setSignupStep('form');
    navigate(getPostAuthDestination());
  };

  const handleForgotPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email) {
      setErrors({ email: 'Please enter your email' });
      return;
    }
    
    setIsLoading(true);
    // Send magic link that auto-signs in and prompts for password reset
    const { error } = await supabase.auth.resetPasswordForEmail(email, {
      redirectTo: `${window.location.origin}/auth`,
    });
    
    if (error) {
      // Don't reveal if email exists or not for security
      const msg = error.message.toLowerCase();
      if (msg.includes('rate limit') || msg.includes('too many')) {
        toast({
          title: 'Too many attempts',
          description: 'Please wait a few minutes before trying again.',
          variant: 'destructive',
        });
      } else {
        // Show success message even on error to prevent email enumeration
        toast({
          title: '📧 Check your inbox',
          description: "If an account exists with this email, you'll receive a secure link to reset your password.",
        });
      }
    } else {
      toast({
        title: '📧 Check your inbox',
        description: "We've sent you a magic link! Click it to securely reset your password – no need to remember your old one.",
      });
      setShowForgotPassword(false);
    }
    setIsLoading(false);
  };

  const handleUpdatePassword = async (e: React.FormEvent) => {
    e.preventDefault();
    if (newPassword.length < 6) {
      toast({
        title: 'Password too short',
        description: 'Password must be at least 6 characters.',
        variant: 'destructive',
      });
      return;
    }
    
    setIsLoading(true);
    const { error } = await supabase.auth.updateUser({ password: newPassword });
    
    if (error) {
      toast({
        title: 'Error',
        description: error.message,
        variant: 'destructive',
      });
    } else {
      toast({
        title: 'Password updated!',
        description: 'You can now login with your new password.',
      });
      setIsRecoveryMode(false);
      navigate('/');
    }
    setIsLoading(false);
  };

  // Shared Google button + divider. Placed at the TOP of each tab (highest-
  // converting option first), followed by an "or with email" separator.
  const googleButton = (
    <Button
        type="button"
        variant="outline"
        className="w-full h-11 font-medium bg-white text-[#1f1f1f] border-gray-300 hover:bg-gray-50 hover:border-gray-400 shadow-sm"
        disabled={isLoading}
        onClick={async () => {
          setIsLoading(true);
          // Remember where to land after sign-in; redirect_uri must stay a
          // public same-origin URL so the session can hydrate first.
          const dest = getPostAuthDestination();
          try {
            sessionStorage.setItem('listbright_post_auth_dest', dest);
          } catch {
            /* ignore storage failures */
          }
          const result = await lovable.auth.signInWithOAuth('google', {
            redirect_uri: window.location.origin,
          });
          if (result.error) {
            toast({
              title: 'Google sign-in failed',
              description: result.error.message ?? 'Please try again.',
              variant: 'destructive',
            });
            setIsLoading(false);
            return;
          }
          if (result.redirected) return;
          navigate(dest);
        }}

      >
        <svg className="w-5 h-5 mr-2" viewBox="0 0 24 24">
          <path
            fill="#4285F4"
            d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
          />
          <path
            fill="#34A853"
            d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
          />
          <path
            fill="#FBBC05"
            d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
          />
          <path
            fill="#EA4335"
            d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
          />
        </svg>
        Continue with Google
      </Button>
  );

  const emailDivider = (
    <div className="relative my-5">
      <div className="absolute inset-0 flex items-center">
        <span className="w-full border-t" />
      </div>
      <div className="relative flex justify-center text-xs uppercase">
        <span className="bg-card px-2 text-muted-foreground tracking-wider">or with email</span>
      </div>
    </div>
  );

  const googleAuthSection = (
    <>
      {emailDivider}
      {googleButton}
    </>
  );

  // Password recovery mode - user clicked magic link
  if (isRecoveryMode) {
    return (
      <Layout showFooter={false}>
        <div className="flex-1 flex items-center justify-center p-4">
          <Card className="w-full max-w-md shadow-elevated border-0">
            <CardHeader className="text-center">
              <div className="flex justify-center mb-4">
                <div className="w-12 h-12 rounded-xl bg-green-100 dark:bg-green-900/30 flex items-center justify-center">
                  <span className="text-2xl">🎉</span>
                </div>
              </div>
              <CardTitle className="font-display text-2xl">You're in!</CardTitle>
              <CardDescription>Now let's set up your new password so you can log in easily next time.</CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleUpdatePassword} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="new-password" className="flex items-center gap-2">
                    <Lock className="w-4 h-4" /> Create new password
                  </Label>
                  <div className="relative">
                    <Input
                      id="new-password"
                      type={showPassword ? 'text' : 'password'}
                      placeholder="At least 8 characters"
                      value={newPassword}
                      onChange={(e) => setNewPassword(e.target.value)}
                      className="pr-10"
                      autoFocus
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
                    >
                      {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                  </div>
                  <p className="text-xs text-muted-foreground">
                    Use at least 8 characters with uppercase, lowercase, and numbers
                  </p>
                </div>
                <Button type="submit" variant="hero" className="w-full" disabled={isLoading}>
                  {isLoading ? 'Saving...' : 'Save & Continue to ListBright →'}
                </Button>
              </form>
            </CardContent>
          </Card>
        </div>
      </Layout>
    );
  }

  // OTP entry step — user typed their email, now enters the 6-digit code inline
  if (signupStep === 'otp') {
    return (
      <Layout showFooter={false}>
        <div className="flex-1 flex items-center justify-center p-4">
          <Card className="w-full max-w-md shadow-elevated border-0">
            <CardHeader className="text-center">
              <div className="flex justify-center mb-4">
                <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center">
                  <Mail className="w-6 h-6 text-primary" />
                </div>
              </div>
              <CardTitle className="font-display text-2xl">Verify your email</CardTitle>
              <CardDescription>
                We sent a 6-digit code to <span className="font-medium text-foreground">{email}</span>.
                Enter it below, then you'll create a password to finish.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <form
                onSubmit={(e) => { e.preventDefault(); handleVerifyOtp(); }}
                className="space-y-5"
              >
                <div className="flex justify-center">
                  <InputOTP
                    maxLength={6}
                    value={otpCode}
                    onChange={(value) => {
                      setOtpCode(value);
                      // Auto-verify the moment all 6 digits are in
                      if (value.length === 6 && !isLoading) handleVerifyOtp(value);
                    }}
                    autoFocus
                    disabled={isLoading}
                  >
                    <InputOTPGroup>
                      {[0, 1, 2, 3, 4, 5].map((index) => (
                        <InputOTPSlot key={index} index={index} className="w-11 h-12 text-lg" />
                      ))}
                    </InputOTPGroup>
                  </InputOTP>
                </div>

                <Button type="submit" variant="hero" className="w-full" disabled={isLoading || otpCode.length !== 6}>
                  {isLoading ? 'Verifying...' : 'Verify & Continue'}
                </Button>

                <div className="text-center text-sm text-muted-foreground space-y-2">
                  <p>
                    Didn't get it?{' '}
                    <button
                      type="button"
                      className="text-primary hover:underline disabled:opacity-50 disabled:no-underline"
                      disabled={isLoading || resendIn > 0}
                      onClick={async () => {
                        const sent = await sendSignupCode();
                        if (sent) {
                          setOtpCode('');
                          toast({ title: 'Code resent', description: `A fresh code is on its way to ${email}.` });
                        }
                      }}
                    >
                      {resendIn > 0 ? `Resend code (${resendIn}s)` : 'Resend code'}
                    </button>
                  </p>
                  <p className="text-xs">Check your spam folder if it doesn't arrive within a minute.</p>
                </div>

                <Button
                  type="button"
                  variant="ghost"
                  className="w-full"
                  onClick={() => { setSignupStep('form'); setOtpCode(''); }}
                >
                  ← Use a different email
                </Button>
              </form>
            </CardContent>
          </Card>
        </div>
      </Layout>
    );
  }

  // Optional set-password step right after OTP verification
  if (signupStep === 'set-password') {
    return (
      <Layout showFooter={false}>
        <div className="flex-1 flex items-center justify-center p-4">
          <Card className="w-full max-w-md shadow-elevated border-0">
            <CardHeader className="text-center">
              <div className="flex justify-center mb-4">
                <div className="w-12 h-12 rounded-xl bg-green-100 dark:bg-green-900/30 flex items-center justify-center">
                  <span className="text-2xl">🎉</span>
                </div>
              </div>
              <CardTitle className="font-display text-2xl">Create your password</CardTitle>
              <CardDescription>
                Almost done — set a password so you can sign back in anytime. You can reset it later via email if you forget it.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleSetSignupPassword} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="signup-set-password" className="flex items-center gap-2">
                    <Lock className="w-4 h-4" /> Password
                  </Label>
                  <div className="relative">
                    <Input
                      id="signup-set-password"
                      type={showPassword ? 'text' : 'password'}
                      placeholder="At least 8 characters"
                      value={newPassword}
                      onChange={(e) => setNewPassword(e.target.value)}
                      className="pr-10"
                      autoFocus
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
                    >
                      {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                  </div>
                  <p className="text-xs text-muted-foreground">
                    Use at least 8 characters. This is what you'll enter next time you log in.
                  </p>
                </div>
                <Button type="submit" variant="hero" className="w-full" disabled={isLoading || newPassword.length < 8}>
                  {isLoading ? 'Saving...' : 'Save password & continue →'}
                </Button>
              </form>
            </CardContent>
          </Card>
        </div>
      </Layout>
    );
  }

  // Forgot password view
  if (showForgotPassword) {
    return (
      <Layout showFooter={false}>
        <div className="flex-1 flex items-center justify-center p-4">
          <Card className="w-full max-w-md shadow-elevated border-0">
            <CardHeader className="text-center">
              <div className="flex justify-center mb-4">
                <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center">
                  <Mail className="w-6 h-6 text-primary" />
                </div>
              </div>
              <CardTitle className="font-display text-2xl">Forgot your password?</CardTitle>
              <CardDescription>No worries! Enter your email and we'll send you a magic link to get back in.</CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleForgotPassword} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="reset-email" className="flex items-center gap-2">
                    <Mail className="w-4 h-4" /> Email address
                  </Label>
                  <Input
                    id="reset-email"
                    type="email"
                    placeholder="your@email.com"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    autoFocus
                  />
                  {errors.email && <p className="text-sm text-destructive">{errors.email}</p>}
                </div>
                
                {/* Info box about magic link */}
                <div className="bg-primary/5 rounded-lg p-3 text-sm text-muted-foreground">
                  <p className="flex items-start gap-2">
                    <span className="shrink-0">✨</span>
                    <span>We'll send you a secure magic link. Just click it and you'll be signed in automatically – then you can set a new password.</span>
                  </p>
                </div>
                
                <Button type="submit" variant="hero" className="w-full" disabled={isLoading}>
                  {isLoading ? 'Sending magic link...' : 'Send Magic Link'}
                </Button>
                <Button 
                  type="button" 
                  variant="ghost" 
                  className="w-full" 
                  onClick={() => setShowForgotPassword(false)}
                >
                  ← Back to Login
                </Button>
              </form>
            </CardContent>
          </Card>
        </div>
      </Layout>
    );
  }

  const initialTab = searchParams.get('mode') === 'login' ? 'login' : 'signup';

  const benefitPoints = [
    { icon: Camera, label: 'AI-enhanced photos in seconds' },
    { icon: FileText, label: 'Ready-to-post descriptions & flyers' },
    { icon: Globe2, label: 'Translations in 28+ languages' },
    { icon: Zap, label: 'First listing ready in ~5 minutes' },
  ];

  return (
    <Layout showFooter={false}>
      <div className="flex-1 flex items-stretch justify-center bg-gradient-to-b from-background to-muted/20">
        <div className="w-full max-w-6xl mx-auto grid lg:grid-cols-2 gap-0 lg:gap-8 px-3 py-4 lg:p-8 items-center">
          {/* Mobile hero banner — punchy value prop + social proof above the form */}
          <div className="lg:hidden -mx-3 -mt-4 mb-4 relative overflow-hidden bg-gradient-to-br from-primary via-primary to-primary/85 text-primary-foreground px-5 pt-8 pb-6">
            <div
              aria-hidden
              className="absolute inset-0 opacity-[0.10]"
              style={{
                backgroundImage:
                  'linear-gradient(hsl(0 0% 100%) 1px, transparent 1px), linear-gradient(90deg, hsl(0 0% 100%) 1px, transparent 1px)',
                backgroundSize: '48px 48px',
              }}
            />
            <div aria-hidden className="absolute -top-16 -right-16 w-56 h-56 rounded-full bg-white/10 blur-3xl" />
            <div className="relative">
              <div className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-white/15 backdrop-blur text-[10px] font-semibold uppercase tracking-wider">
                <Sparkles className="w-3 h-3" />
                Free trial listing
              </div>
              <h1 className="mt-3 text-2xl font-display font-bold leading-tight">
                Sell listings faster,<br />for more.
              </h1>
              <p className="mt-2 text-sm text-primary-foreground/85">
                Turn phone photos into a full listing — photos, copy, flyer & microsite — in ~5 minutes.
              </p>
              <div className="mt-4 flex items-center gap-3 rounded-xl bg-white/10 backdrop-blur border border-white/15 p-3">
                <img src={portraitMarcus} alt="Marcus Thompson" width={36} height={36} className="w-9 h-9 rounded-full object-cover ring-2 ring-white/30 shrink-0" />
                <div className="min-w-0">
                  <div className="flex items-center gap-0.5 mb-0.5">
                    {[0,1,2,3,4].map((i) => (
                      <Star key={i} className="w-3 h-3 fill-yellow-300 text-yellow-300" />
                    ))}
                  </div>
                  <p className="text-xs leading-snug text-primary-foreground/95 line-clamp-2">
                    "Paid for itself on my first sale."
                  </p>
                  <div className="text-[10px] text-primary-foreground/70 mt-0.5 truncate">Marcus T. · Keller Williams</div>
                </div>
              </div>
            </div>
          </div>

          {/* Left: value proposition (desktop) */}
          <aside className="hidden lg:flex relative flex-col justify-between overflow-hidden rounded-3xl bg-gradient-to-br from-primary via-primary to-primary/80 text-primary-foreground p-10 min-h-[640px] shadow-elevated">
            <div
              aria-hidden
              className="absolute inset-0 opacity-[0.12]"
              style={{
                backgroundImage:
                  'linear-gradient(hsl(0 0% 100%) 1px, transparent 1px), linear-gradient(90deg, hsl(0 0% 100%) 1px, transparent 1px)',
                backgroundSize: '64px 64px',
              }}
            />
            <div aria-hidden className="absolute -top-24 -right-24 w-72 h-72 rounded-full bg-white/10 blur-3xl" />
            <div aria-hidden className="absolute -bottom-24 -left-24 w-80 h-80 rounded-full bg-white/10 blur-3xl" />

            <div className="relative">
              <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white/15 backdrop-blur text-xs font-medium">
                <Sparkles className="w-3.5 h-3.5" />
                Join thousands of agents & owners
              </div>
              <h2 className="mt-6 text-3xl xl:text-4xl font-display font-bold leading-tight">
                Sell listings faster,<br />for more.
              </h2>
              <p className="mt-3 text-primary-foreground/80 text-base max-w-sm">
                Turn a few phone photos into a full, polished listing — photos, copy, flyer and microsite — in minutes.
              </p>

              <ul className="mt-8 space-y-3.5">
                {benefitPoints.map(({ icon: Icon, label }) => (
                  <li key={label} className="flex items-center gap-3">
                    <div className="w-9 h-9 rounded-lg bg-white/15 backdrop-blur flex items-center justify-center shrink-0">
                      <Icon className="w-4.5 h-4.5" />
                    </div>
                    <span className="text-sm font-medium text-primary-foreground/95">{label}</span>
                  </li>
                ))}
              </ul>
            </div>

            <div className="relative mt-10">
              <div className="rounded-2xl bg-white/10 backdrop-blur border border-white/15 p-5">
                <div className="flex items-center gap-1 mb-2">
                  {[0,1,2,3,4].map((i) => (
                    <Star key={i} className="w-4 h-4 fill-yellow-300 text-yellow-300" />
                  ))}
                </div>
                <p className="text-sm leading-relaxed text-primary-foreground/95">
                  "My listings used to sit for weeks. Now the photos pop on Zillow and I get showings within days — ListBright paid for itself on my first sale."
                </p>
                <div className="mt-4 flex items-center gap-3">
                  <img src={portraitMarcus} alt="Marcus Thompson" width={40} height={40} className="w-10 h-10 rounded-full object-cover ring-2 ring-white/30" />
                  <div>
                    <div className="text-sm font-semibold">Marcus Thompson</div>
                    <div className="text-xs text-primary-foreground/70">Realtor · Keller Williams, Austin</div>
                  </div>
                </div>
              </div>

              <div className="mt-5 flex items-center gap-4 text-xs text-primary-foreground/80">
                <div className="flex items-center gap-1.5"><ShieldCheck className="w-3.5 h-3.5" /> No credit card</div>
                <div className="flex items-center gap-1.5"><Zap className="w-3.5 h-3.5" /> Free to try</div>
                <div className="flex items-center gap-1.5"><Globe2 className="w-3.5 h-3.5" /> 40+ countries</div>
              </div>
            </div>
          </aside>

          {/* Right: auth card */}
          <Card className="w-full max-w-md mx-auto lg:mx-0 shadow-elevated border-border/40">
          <CardHeader className="text-center pb-3 px-5 lg:px-6 pt-5 lg:pt-6">
            <div className="flex justify-center mb-2 lg:mb-3">
              <div className="w-10 h-10 lg:w-12 lg:h-12 rounded-xl bg-primary/10 flex items-center justify-center">
                <Home className="w-5 h-5 lg:w-6 lg:h-6 text-primary" />
              </div>
            </div>
            <CardTitle className="font-display text-xl lg:text-2xl">Create your free account</CardTitle>
            <CardDescription className="text-sm">Your first listing is on us — no credit card required.</CardDescription>

            {/* Mobile trust row */}
            <div className="lg:hidden mt-3 flex items-center justify-center gap-2.5 text-[11px] text-muted-foreground">
              <span className="flex items-center gap-1"><ShieldCheck className="w-3 h-3" /> No card</span>
              <span className="opacity-40">·</span>
              <span className="flex items-center gap-1"><Zap className="w-3 h-3" /> ~5 min setup</span>
              <span className="opacity-40">·</span>
              <span className="flex items-center gap-1"><Star className="w-3 h-3 fill-yellow-500 text-yellow-500" /> 4.9/5</span>
            </div>
          </CardHeader>
          <CardContent className="px-5 lg:px-6 pb-5 lg:pb-6">
            <Tabs defaultValue={initialTab} className="w-full">
              <TabsList className="grid w-full grid-cols-2 mb-4 lg:mb-6">
                <TabsTrigger value="signup">Sign Up</TabsTrigger>
                <TabsTrigger value="login">Login</TabsTrigger>
              </TabsList>
              
              <TabsContent value="login">
                {googleButton}
                {emailDivider}
                <form onSubmit={handleLogin} className="space-y-4">
                    <div className="space-y-2">
                      <Label htmlFor="login-email" className="flex items-center gap-2">
                        <Mail className="w-4 h-4" /> Email
                      </Label>
                      <Input
                        id="login-email"
                        type="email"
                        placeholder="your@email.com"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                      />
                      {errors.email && <p className="text-sm text-destructive">{errors.email}</p>}
                    </div>
                    <div className="space-y-2">
                      <div className="flex items-center justify-between">
                        <Label htmlFor="login-password" className="flex items-center gap-2">
                          <Lock className="w-4 h-4" /> Password
                        </Label>
                        <button
                          type="button"
                          onClick={() => setShowForgotPassword(true)}
                          className="text-sm text-primary hover:underline"
                        >
                          Forgot password?
                        </button>
                      </div>
                      <div className="relative">
                        <Input
                          id="login-password"
                          type={showPassword ? 'text' : 'password'}
                          placeholder="••••••••"
                          value={password}
                          onChange={(e) => setPassword(e.target.value)}
                          className="pr-10"
                        />
                        <button
                          type="button"
                          onClick={() => setShowPassword(!showPassword)}
                          className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
                        >
                          {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                        </button>
                      </div>
                      {errors.password && <p className="text-sm text-destructive">{errors.password}</p>}
                    </div>
                    <Button type="submit" variant="hero" className="w-full" disabled={isLoading}>
                      {isLoading ? 'Logging in...' : 'Login'}
                    </Button>
                    <p className="text-center text-xs text-muted-foreground pt-1">
                      Signed up recently but never set a password?{' '}
                      <button
                        type="button"
                        onClick={() => setShowForgotPassword(true)}
                        className="text-primary hover:underline"
                      >
                        Set one via email
                      </button>
                    </p>
                </form>
            </TabsContent>
            
            <TabsContent value="signup">
              {googleButton}
              {emailDivider}
              <form onSubmit={handleSignUp} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="signup-email" className="flex items-center gap-2">
                    <Mail className="w-4 h-4" /> Email
                  </Label>
                  <Input
                    id="signup-email"
                    type="email"
                    placeholder="your@email.com"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                  />
                  {errors.email && <p className="text-sm text-destructive">{errors.email}</p>}
                  <p className="text-xs text-muted-foreground">
                    We'll email you a 6-digit code to verify. You'll set a password on the next step.
                  </p>
                </div>

                <Button type="submit" variant="hero" className="w-full" disabled={isLoading || !acceptTerms}>
                  {isLoading ? 'Sending code...' : 'Create free account →'}
                </Button>

                {/* Compact terms + marketing consent, moved below CTA to reduce friction */}
                <div className="space-y-2.5 pt-1">
                  <div className="flex items-start gap-2.5">
                    <Checkbox
                      id="accept-terms"
                      checked={acceptTerms}
                      onCheckedChange={(checked) => setAcceptTerms(checked as boolean)}
                      className="mt-0.5"
                    />
                    <Label htmlFor="accept-terms" className="text-xs leading-relaxed cursor-pointer text-muted-foreground">
                      I agree to the{' '}
                      <Link to="/terms" className="text-primary hover:underline font-medium" target="_blank">
                        Terms
                      </Link>{' '}
                      &{' '}
                      <Link to="/privacy" className="text-primary hover:underline font-medium" target="_blank">
                        Privacy Policy
                      </Link>
                      , including disclosure of AI-enhanced photos in my listings.
                    </Label>
                  </div>

                  <div className="flex items-start gap-2.5">
                    <Checkbox
                      id="accept-marketing"
                      checked={acceptMarketing}
                      onCheckedChange={(checked) => setAcceptMarketing(checked as boolean)}
                      className="mt-0.5"
                    />
                    <Label htmlFor="accept-marketing" className="text-xs leading-relaxed cursor-pointer text-muted-foreground">
                      Send me product updates & real estate tips (optional).
                    </Label>
                  </div>
                </div>
              </form>
            </TabsContent>
          </Tabs>
        </CardContent>
      </Card>
        </div>
      </div>
  </Layout>
  );
}
